import React from "react";
import { Chip, Button } from "@mui/material";
import LogoImage from "assets/images/logo.svg";
import { useTranslation } from "react-i18next";
import UserNavItem from "components/AppHeader/UserNavItem";
import { useScreenWidthMatch } from "hooks/useScreenWidthMatch";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router";
import FlexBox from "components/FlexBox/FlexBox";
import { BUILD_API_ENDPOINT, NOTIFICATION_CENTRE_ENABLED, USE_MOCK_BACKEND } from "constants/configs";
import NotificationNavItem from "components/AppHeader/NotificationNavItem";
import Guard from "components/Guard/Guard";
import HelpNavItem from "components/AppHeader/HelpNavItem";
import { css } from "@emotion/react";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";
import { hideInPrintMixin } from "styles/mixins/hideInPrintMixin";

type Props = {};

const AppHeader: React.FC<Props> = () => {
  const { t } = useTranslation();
  const screenWidthMatch = useScreenWidthMatch();
  const history = useHistory();

  const handleClickNewApplication = React.useCallback(() => {
    history.push("/new-application");
  }, [history]);

  const handleClickDashboard = React.useCallback(() => {
    history.push("/");
  }, [history]);

  return (
    <StyledAppHeaderContainer>
      <FlexBox spacing={4} alignItems={"center"}>
        <StyledH1>Objective Build</StyledH1>
        <StyledLogoImage src={LogoImage} alt={"Objective Build"} onClick={handleClickDashboard} />
        {USE_MOCK_BACKEND && <Chip color="secondary" label="Mock" />}
        {BUILD_API_ENDPOINT.includes("localhost") && <Chip color="primary" label="Local" />}
      </FlexBox>

      <StyledToolBar>
        <Guard condition={screenWidthMatch.md}>
          <Button
            variant={"text"}
            size={"large"}
            startIcon={<StyledFontIcon name={"i-navigation-add"} />}
            onClick={handleClickNewApplication}
            data-testid="NewApplicationButton"
          >
            {t(`New application`)}
          </Button>
        </Guard>

        <HelpNavItem />

        <Guard condition={NOTIFICATION_CENTRE_ENABLED}>
          <NotificationNavItem />
        </Guard>

        <UserNavItem compact={!screenWidthMatch.md} />
      </StyledToolBar>
    </StyledAppHeaderContainer>
  );
};

const StyledAppHeaderContainer = styled("div")(
  ({ theme }) => css`
    width: 100%;
    height: 73px;
    border-top: 5px solid ${theme.palette.objective.brandColors.build.primary};
    border-bottom: 1px solid ${theme.palette.background.default};
    background-color: ${theme.palette.objective.light.white};

    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    z-index: ${theme.zIndex.appBar};

    ${theme.breakpoints.down("xs")} {
      padding: 0 8px;
    }
  `
);

const StyledLogoImage = styled("img")(
  ({ theme }) => css`
    width: 160px;
    height: 24px;
    cursor: pointer;
  `
);

const StyledToolBar = styled("div")(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    ${theme.mixins.flexGap("12px")};

    ${theme.breakpoints.down("xs")} {
      ${theme.mixins.flexGap("4px")};
    }

    ${hideInPrintMixin()}
  `
);

const StyledH1 = styled("h1")(
  ({ theme }) => css`
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `
);

const StyledFontIcon = styled(FontIcon)(
  ({ theme }) => css`
    font-size: 24px !important;
    width: 24px;
    height: 24px;
  `
);

export default AppHeader;
